import { SAMPLE_IFCS } from './loadSampleIfc'

// ---------------------------------------------------------------------------
// deepLink: interpreta a query string de arranque para abrir o viewer já
// com um modelo carregado (ligações partilháveis para demos da
// buildingSMART Portugal). Funções puras, sem efeitos colaterais, para
// serem testáveis isoladamente. O carregamento em si vive no
// DeepLinkLoader.
// ---------------------------------------------------------------------------

export type DeepLink =
  | { kind: 'sample'; id: string; ids?: string }
  | { kind: 'url'; url: string; ids?: string }

/** Valida o parâmetro `?ids=`: só URL absoluta https: (mesmas regras do
 *  ifc). Devolve o URL se válido, senão undefined. Um ids inválido é
 *  simplesmente ignorado (não invalida o resto da ligação). */
function parseIdsParam(params: URLSearchParams): string | undefined {
  const ids = params.get('ids')
  if (ids === null) return undefined
  try {
    const u = new URL(ids)
    if (u.protocol === 'https:') return ids
  } catch {
    /* URL inválida -> ignora */
  }
  return undefined
}

/** Interpreta a query string (ex.: '?sample=house' ou '?ifc=https://...').
 *  Regras: sample tem de existir em SAMPLE_IFCS (senão null); ifc tem de
 *  ser URL absoluta https: (senão null; http/file/data recusados);
 *  sample tem precedência se vierem os dois. O parâmetro opcional `?ids=`
 *  (URL https: para um ficheiro .ids) acompanha ambos os kinds; se
 *  inválido é ignorado sem invalidar a ligação. */
export function parseDeepLink(search: string): DeepLink | null {
  const params = new URLSearchParams(search)
  const ids = parseIdsParam(params)

  // Sample tem precedência: se vier, decide sozinho (válido -> sample,
  // inválido -> null), sem cair para o ifc.
  const sample = params.get('sample')
  if (sample !== null) {
    return SAMPLE_IFCS.some((s) => s.id === sample)
      ? { kind: 'sample', id: sample, ...(ids ? { ids } : {}) }
      : null
  }

  const ifc = params.get('ifc')
  if (ifc !== null) {
    try {
      const u = new URL(ifc)
      // Só https absoluto: http (inseguro), file: e data: (podem
      // esconder payloads) e relativos são recusados.
      if (u.protocol === 'https:') {
        return { kind: 'url', url: ifc, ...(ids ? { ids } : {}) }
      }
    } catch {
      /* URL inválida -> null */
    }
    return null
  }

  return null
}

/** Constrói a ligação partilhável para um sample: origin + pathname base
 *  + ?sample=<id>. Se `idsUrl` for fornecido (URL https: de um .ids),
 *  acrescenta &ids=<url codificado> para que a ligação abra o modelo já
 *  com o ficheiro IDS pronto a avaliar. */
export function sampleShareUrl(
  origin: string,
  id: string,
  idsUrl?: string,
): string {
  const base = `${origin}/?sample=${encodeURIComponent(id)}`
  return idsUrl ? `${base}&ids=${encodeURIComponent(idsUrl)}` : base
}
